import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import {
  ArrowLeftRight, Check, X, Clock, AlertCircle, User, Calendar, ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ShiftSwaps() {
  const { user } = useAuth();
  const [swapRequests, setSwapRequests] = useState([]);
  const [myShifts, setMyShifts] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [swapDialog, setSwapDialog] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [swapReason, setSwapReason] = useState('');
  const [preferredCaregiver, setPreferredCaregiver] = useState('');
  const [approveDialog, setApproveDialog] = useState(false);
  const [selectedSwap, setSelectedSwap] = useState(null);
  const [newCaregiverId, setNewCaregiverId] = useState('');
  const [filter, setFilter] = useState('all');
  const isAdmin = ['agency_admin', 'ceo'].includes(user?.role);

  useEffect(() => {
    fetchSwapRequests();
    if (user?.role === 'caregiver') fetchMyShifts();
    if (isAdmin) fetchCaregivers();
  }, [user]);

  const fetchSwapRequests = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/shifts/swap-requests`, { withCredentials: true });
      setSwapRequests(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const fetchMyShifts = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/shifts`, { withCredentials: true });
      setMyShifts(data.filter(s => s.caregiver_id === user?.id && ['assigned', 'in_progress'].includes(s.status)));
    } catch (err) { console.error(err); }
  };

  const fetchCaregivers = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/users/by-org?role=caregiver`, { withCredentials: true });
      setCaregivers(data);
    } catch (err) { console.error(err); }
  };

  const handleRequestSwap = async () => {
    if (!selectedShift) return;
    try {
      await axios.post(`${API_URL}/api/shifts/swap-request`, {
        shift_id: selectedShift.id,
        reason: swapReason,
        preferred_caregiver_id: preferredCaregiver || null
      }, { withCredentials: true });
      toast.success('Swap request submitted');
      setSwapDialog(false);
      setSelectedShift(null);
      setSwapReason('');
      setPreferredCaregiver('');
      fetchSwapRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to request swap');
    }
  };

  const handleApprove = async () => {
    if (!selectedSwap) return;
    try {
      await axios.put(`${API_URL}/api/shifts/swap-requests/${selectedSwap.id}/approve`,
        { new_caregiver_id: newCaregiverId || null },
        { withCredentials: true }
      );
      toast.success('Swap approved');
      setApproveDialog(false);
      setSelectedSwap(null);
      setNewCaregiverId('');
      fetchSwapRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleDeny = async (swapId) => {
    try {
      await axios.put(`${API_URL}/api/shifts/swap-requests/${swapId}/deny`,
        { reason: 'Denied by admin' },
        { withCredentials: true }
      );
      toast.success('Swap request denied');
      fetchSwapRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to deny');
    }
  };

  const filteredSwaps = filter === 'all' ? swapRequests : swapRequests.filter(s => s.status === filter);

  const statusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      approved: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      denied: 'bg-red-500/10 text-red-400 border-red-500/20',
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.pending}`}>
        {status === 'pending' && <Clock className="w-3 h-3" />}
        {status === 'approved' && <Check className="w-3 h-3" />}
        {status === 'denied' && <X className="w-3 h-3" />}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="shift-swaps-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
              <ArrowLeftRight className="w-7 h-7 inline mr-3 text-[#FF6F61]" />
              Shift Swaps
            </h1>
            <p className="text-muted-foreground mt-1">
              {isAdmin ? 'Review and manage swap requests from caregivers' : 'Request to swap your assigned shifts'}
            </p>
          </div>
          {user?.role === 'caregiver' && (
            <Button className="btn-primary" onClick={() => setSwapDialog(true)} data-testid="request-swap-btn">
              <ArrowLeftRight className="w-4 h-4 mr-2" />Request Swap
            </Button>
          )}
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 flex-wrap">
          {['all', 'pending', 'approved', 'denied'].map(f => (
            <Button key={f} variant={filter === f ? 'default' : 'outline'}
              className={filter === f ? 'bg-[#00A6A0] text-[#0F1419] hover:bg-[#008A85]' : 'border-border text-muted-foreground hover:bg-muted/50'}
              size="sm" onClick={() => setFilter(f)} data-testid={`filter-${f}`}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
              {f === 'pending' && swapRequests.filter(s => s.status === 'pending').length > 0 && (
                <span className="ml-1.5 bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded-full text-[10px]">
                  {swapRequests.filter(s => s.status === 'pending').length}
                </span>
              )}
            </Button>
          ))}
        </div>

        {/* Swap Requests List */}
        <Card className="glass-card border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="text-lg font-heading text-foreground">
              {isAdmin ? 'Swap Requests' : 'My Swap Requests'} ({filteredSwaps.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-12 text-center text-muted-foreground">Loading...</div>
            ) : filteredSwaps.length === 0 ? (
              <div className="p-12 text-center">
                <ArrowLeftRight className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {filter === 'all' ? 'No swap requests yet' : `No ${filter} requests`}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {filteredSwaps.map((swap) => (
                  <div key={swap.id} className="p-5 hover:bg-muted/50 transition-colors" data-testid={`swap-request-${swap.id}`}>
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-11 h-11 rounded-xl bg-[#FF6F61]/10 flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-[#FF6F61]" />
                        </div>
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-foreground font-medium">{swap.requesting_caregiver_name}</p>
                            {statusBadge(swap.status)}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              {swap.shift_date}
                            </span>
                            <span>{swap.shift_start} - {swap.shift_end}</span>
                            {swap.client_name && <span>Client: {swap.client_name}</span>}
                          </div>
                          {swap.reason && (
                            <p className="text-xs text-muted-foreground">Reason: {swap.reason}</p>
                          )}
                          {swap.new_caregiver_name && (
                            <p className="text-xs text-[#A8E6CF]">Reassigned to: {swap.new_caregiver_name}</p>
                          )}
                          {swap.denial_reason && (
                            <p className="text-xs text-red-400">Denial reason: {swap.denial_reason}</p>
                          )}
                        </div>
                      </div>

                      {/* Admin Actions */}
                      {isAdmin && swap.status === 'pending' && (
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => { setSelectedSwap(swap); setApproveDialog(true); }}
                            data-testid={`approve-swap-${swap.id}`}>
                            <Check className="w-3.5 h-3.5 mr-1" />Approve
                          </Button>
                          <Button size="sm" variant="outline"
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                            onClick={() => handleDeny(swap.id)}
                            data-testid={`deny-swap-${swap.id}`}>
                            <X className="w-3.5 h-3.5 mr-1" />Deny
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Caregiver: Request Swap Dialog */}
        <Dialog open={swapDialog} onOpenChange={setSwapDialog}>
          <DialogContent className="glass-card border-border max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl font-heading text-foreground">Request Shift Swap</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Select Shift to Swap</label>
                <Select value={selectedShift?.id || ''} onValueChange={(val) => setSelectedShift(myShifts.find(s => s.id === val))}>
                  <SelectTrigger className="bg-muted border-border text-foreground" data-testid="select-shift-to-swap">
                    <SelectValue placeholder="Choose a shift..." />
                  </SelectTrigger>
                  <SelectContent className="glass-card border-border">
                    {myShifts.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.date} | {s.start_time}-{s.end_time} | {s.client_name || 'Client'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Reason (optional)</label>
                <textarea
                  value={swapReason}
                  onChange={(e) => setSwapReason(e.target.value)}
                  placeholder="Why do you need to swap this shift?"
                  className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground placeholder:text-muted-foreground text-sm min-h-[80px] focus:ring-1 focus:ring-[#00A6A0] focus:outline-none"
                  data-testid="swap-reason-input"
                />
              </div>
              <Button className="w-full btn-primary" onClick={handleRequestSwap}
                disabled={!selectedShift} data-testid="submit-swap-request-btn">
                Submit Swap Request
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Admin: Approve Dialog with Caregiver Selection */}
        <Dialog open={approveDialog} onOpenChange={setApproveDialog}>
          <DialogContent className="glass-card border-border max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl font-heading text-foreground">Approve Swap Request</DialogTitle>
            </DialogHeader>
            {selectedSwap && (
              <div className="space-y-4 py-2">
                <div className="p-4 rounded-lg bg-muted border border-border space-y-2">
                  <p className="text-foreground font-medium">{selectedSwap.requesting_caregiver_name}'s Shift</p>
                  <p className="text-sm text-muted-foreground">{selectedSwap.shift_date} | {selectedSwap.shift_start}-{selectedSwap.shift_end}</p>
                  <p className="text-sm text-muted-foreground">Client: {selectedSwap.client_name || 'N/A'}</p>
                  {selectedSwap.reason && <p className="text-xs text-muted-foreground">Reason: {selectedSwap.reason}</p>}
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">Assign to new caregiver (optional — leave empty to open shift)</label>
                  <Select value={newCaregiverId} onValueChange={setNewCaregiverId}>
                    <SelectTrigger className="bg-muted border-border text-foreground" data-testid="select-new-caregiver">
                      <SelectValue placeholder="Leave open or select..." />
                    </SelectTrigger>
                    <SelectContent className="glass-card border-border">
                      {caregivers
                        .filter(c => c.id !== selectedSwap.requesting_caregiver_id)
                        .map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name} ({c.email})</SelectItem>
                        ))
                      }
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-3">
                  <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleApprove}
                    data-testid="confirm-approve-swap-btn">
                    <Check className="w-4 h-4 mr-2" />Approve & Reassign
                  </Button>
                  <Button variant="outline" className="border-border text-muted-foreground" onClick={() => setApproveDialog(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
